import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@chronosphereio/chrono-ui';
import { Select, SelectProps } from '@mui/material';
import { FormEventHandler, useState } from 'react';
import { PanelGroupDefinition, PanelGroupEditorValues } from '../../context';
import { VariableAutocomplete } from '../VariableAutocomplete';

type CollapsedState = 'Open' | 'Closed';

export interface PanelGroupEditorFormProps {
  initialValues: PanelGroupEditorValues;
  onSubmit: (next: PanelGroupEditorValues) => void;
  kind: PanelGroupDefinition['kind'];
}

export function PanelGroupEditorForm(props: PanelGroupEditorFormProps) {
  const { initialValues, onSubmit, kind } = props;

  const [title, setTitle] = useState(initialValues.title);
  const [isCollapsed, setIsCollapsed] = useState(initialValues.isCollapsed);

  const [showSplitVariableState, setShowSplitVariable] = useState(initialValues.splitEnabled);
  const [splitVariableName, setSplitVariableName] = useState(initialValues.splitVariableName);

  const showSplitVariable = kind === 'SplitPanel' ? true : showSplitVariableState;

  const handleCollapsedChange: SelectProps<CollapsedState>['onChange'] = (e) => {
    const next = e.target.value;
    setIsCollapsed(next === 'Closed');
  };

  const handleSplitToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isSplitGroupToggled = e.target.checked;
    setShowSplitVariable(isSplitGroupToggled);
    if (!isSplitGroupToggled) {
      // Reset splitVariableName when toggle is off
      setSplitVariableName(undefined);
    }
  };

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    onSubmit({ title, isCollapsed, splitEnabled: showSplitVariable, splitVariableName });
  };

  return (
    <form id={panelGroupEditorFormId} onSubmit={handleSubmit}>
      <FormControl fullWidth margin="normal">
        <TextField label="Name" value={title} onChange={(e) => setTitle(e.target.value)} />
      </FormControl>
      <FormControl fullWidth margin="normal">
        <InputLabel id="select-collapse-state">Collapse state</InputLabel>
        <Select<CollapsedState>
          required
          displayEmpty
          labelId="select-collapse-state"
          label="Collapse state"
          value={isCollapsed ? 'Closed' : 'Open'}
          onChange={handleCollapsedChange}
        >
          <MenuItem value="Open">Open</MenuItem>
          <MenuItem value="Closed">Closed</MenuItem>
        </Select>
      </FormControl>

      <Divider sx={{ margin: (theme) => theme.spacing(2, 0, 0) }} />
      {kind === 'Grid' && (
        <>
          <FormControl fullWidth margin="normal">
            <Box display="flex">
              <FormControlLabel
                control={<Switch checked={showSplitVariable} onChange={handleSplitToggleChange} />}
                label="Split Panel Group"
                labelPlacement="start"
                sx={{ margin: 0 }}
              />
            </Box>
            <FormHelperText sx={{ margin: 0 }}>
              Dynamically generate panel groups for each value of the selected variable.
            </FormHelperText>
          </FormControl>
          {showSplitVariable && <VariableAutocomplete value={splitVariableName} onChange={setSplitVariableName} />}
        </>
      )}

      {kind === 'SplitPanel' && (
        <FormControl fullWidth margin="normal">
          <Typography variant="body1">Split Panel Options</Typography>
          <Box display="flex">
            <VariableAutocomplete value={splitVariableName} onChange={setSplitVariableName} />
          </Box>
          <FormHelperText sx={{ margin: 0 }}>
            Dynamically generate panels for each value of the selected variable.
          </FormHelperText>
        </FormControl>
      )}
    </form>
  );
}

/**
 * The `id` attribute added to the `PanelGroupEditorForm` component, allowing submit buttons to live outside the form.
 */
export const panelGroupEditorFormId = 'panel-group-editor-form';
